import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiService } from '../../services/apiService';

interface ChatState {
  sessions: any[];
  currentSession: any;
  messages: any[];
  loading: boolean;
  activeRequestId: string | null;
  error: string | null;
  lastDetectedBills: string[];
  lastRetrievalDebug?: any | null;
}

const initialState: ChatState = {
  sessions: [],
  currentSession: null,
  messages: [],
  loading: false,
  activeRequestId: null,
  error: null,
  lastDetectedBills: [],
  lastRetrievalDebug: null,
};

export const sendMessage = createAsyncThunk(
  'chat/sendMessage',
  async (params: { message: string; sessionId?: string; billContext?: string[]; memberContext?: string[] }, { rejectWithValue }) => {
    try {
      const response = await apiService.sendChatMessage(params);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data?.detail || error?.message || 'The chat service is temporarily unavailable');
    }
  }
);

export const createChatSession = createAsyncThunk(
  'chat/createSession',
  async (params: { billContext?: string[]; memberContext?: string[] } | undefined) => {
    const response = await apiService.createChatSession(params?.billContext || [], params?.memberContext || []);
    return response.data;
  }
);

export const fetchChatSessions = createAsyncThunk(
  'chat/fetchSessions',
  async () => {
    const response = await apiService.getChatSessions();
    return response.data;
  }
);

export const fetchChatHistory = createAsyncThunk(
  'chat/fetchHistory',
  async (sessionId: string) => {
    const response = await apiService.getChatHistory(sessionId);
    return response.data;
  }
);

export const deleteChatSessionThunk = createAsyncThunk(
  'chat/deleteSession',
  async (sessionId: string) => {
    const response = await apiService.deleteChatSession(sessionId);
    return { sessionId, data: response.data };
  }
);

const chatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    setCurrentSession: (state, action) => {
      state.currentSession = action.payload;
    },
    addMessage: (state, action) => {
      state.messages.push(action.payload);
    },
    clearMessages: (state) => {
      state.messages = [];
      state.activeRequestId = null;
      state.loading = false;
      state.error = null;
      state.lastDetectedBills = [];
      state.lastRetrievalDebug = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(sendMessage.pending, (state, action) => {
        state.activeRequestId = action.meta.requestId;
        state.loading = true;
        state.error = null;
      })
      .addCase(sendMessage.fulfilled, (state, action) => {
        if (state.activeRequestId !== action.meta.requestId) return;
        state.activeRequestId = null;
        state.loading = false;
        // Add AI response
        state.messages.push({
          role: 'assistant',
          content: action.payload.response,
          timestamp: action.payload.timestamp,
        });
        // Capture any detected bills from the backend to allow the UI to auto-focus PDF
        try {
          // Accept both camelCase and snake_case to be resilient to backend variations
          const detected = (action.payload.detectedBills || action.payload.detected_bills) as string[] | undefined;
          state.lastDetectedBills = Array.isArray(detected) ? detected : [];
        } catch {
          state.lastDetectedBills = [];
        }
        // Store retrieval debug (citations & confidence) if available
        try {
          state.lastRetrievalDebug = action.payload.retrieval_debug || action.payload.retrievalDebug || null;
        } catch {
          state.lastRetrievalDebug = null;
        }
        if (!state.currentSession && action.payload.session_id) {
          state.currentSession = { session_id: action.payload.session_id };
        }
      })
      .addCase(createChatSession.fulfilled, (state, action) => {
        // Set current session immediately when created
        state.currentSession = { session_id: action.payload.session_id };
        // Optimistically add to sessions list if present
        const exists = state.sessions?.some((s:any) => s.session_id === action.payload.session_id);
        if (!exists) {
          state.sessions = [action.payload, ...(state.sessions || [])];
        }
      })
      .addCase(sendMessage.rejected, (state, action) => {
        if (state.activeRequestId !== action.meta.requestId) return;
        state.activeRequestId = null;
        state.loading = false;
        state.error = typeof action.payload === 'string' ? action.payload : (action.error.message || 'Failed to send message');
      })
      .addCase(fetchChatSessions.fulfilled, (state, action) => {
        state.sessions = action.payload;
      })
      .addCase(fetchChatHistory.fulfilled, (state, action) => {
        state.messages = action.payload.messages;
      })
      .addCase(deleteChatSessionThunk.fulfilled, (state, action) => {
        const sid = action.payload.sessionId;
        state.sessions = (state.sessions || []).filter((s:any) => s.session_id !== sid);
        if (state.currentSession && state.currentSession.session_id === sid) {
          state.currentSession = null;
          state.messages = [];
        }
      });
  },
});

export const { setCurrentSession, addMessage, clearMessages, clearError } = chatSlice.actions;
export default chatSlice.reducer;
